/**
 * El Programa de socios contra job-apply-service:
 *
 *   GET  /job-apply/api/partner              la solicitud o el panel
 *   POST /job-apply/api/partner/apply        enviar la solicitud
 *   POST /job-apply/api/partner/accept-terms re-aceptar los términos (versión vigente)
 *   POST /job-apply/api/partner/payouts-link "Configurar mis cobros"
 *
 * Estados y no `throw`, como `couponClient`: cada respuesta se le dice distinto
 * a la persona ("ese código ya es de alguien" no es "falló, inténtalo de nuevo").
 */
import { apiFetch } from '~/lib/api';
import { readTermsError, termsAcceptanceBody, type TermsError } from '~/lib/termsVersions';
import type { PartnerField, PartnerForm, PartnerResponse, PartnerView } from '../lib/partner';

export type LoadPartnerResult = { status: 'ok'; data: PartnerResponse } | { status: 'failed' };

export async function loadPartner(): Promise<LoadPartnerResult> {
  try {
    const res = await apiFetch('/job-apply/api/partner');
    if (!res.ok) return { status: 'failed' };
    const data = (await res.json()) as PartnerResponse;
    return { status: 'ok', data };
  } catch {
    return { status: 'failed' };
  }
}

export type ApplyPartnerResult =
  | { status: 'ok'; partner: PartnerView }
  /** 400: los campos que el servidor no aceptó. */
  | { status: 'invalid'; fields: PartnerField[] }
  /** 409: el código ya es de otro socio o de un cupón de Stripe. */
  | { status: 'code_taken' }
  /** 400 de los términos: falta la casilla, o la versión ya no es la vigente. */
  | { status: 'terms'; error: TermsError }
  /** 5xx, 502 de Stripe o la red caída. */
  | { status: 'failed' };

/** La solicitud, con la aceptación de los términos del programa en la versión que se mostró. */
export async function applyPartner(form: PartnerForm, termsVersion: string): Promise<ApplyPartnerResult> {
  let res: Response;
  try {
    res = await apiFetch('/job-apply/api/partner/apply', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...form, ...termsAcceptanceBody(termsVersion) }),
    });
  } catch {
    return { status: 'failed' };
  }
  const body = (await res.json().catch(() => ({}))) as {
    partner?: PartnerView;
    code?: string;
    fields?: PartnerField[];
  };
  if (res.ok && body.partner) return { status: 'ok', partner: body.partner };
  const termsError = readTermsError(res.status, body);
  if (termsError) return { status: 'terms', error: termsError };
  if (res.status === 409 || body.code === 'code_taken') return { status: 'code_taken' };
  if (res.status === 400) return { status: 'invalid', fields: body.fields ?? [] };
  return { status: 'failed' };
}

export type PayoutsLinkResult =
  | { status: 'ok'; url: string; ready: boolean }
  /** 422: el país de la solicitud no lo paga Stripe. Repetir no lo arregla. */
  | { status: 'unsupported_country' }
  | { status: 'failed' };

export async function requestPayoutsLink(): Promise<PayoutsLinkResult> {
  let res: Response;
  try {
    res = await apiFetch('/job-apply/api/partner/payouts-link', { method: 'POST' });
  } catch {
    return { status: 'failed' };
  }
  const body = (await res.json().catch(() => ({}))) as { url?: string; ready?: boolean; code?: string };
  if (res.ok && typeof body.url === 'string' && body.url) {
    return { status: 'ok', url: body.url, ready: Boolean(body.ready) };
  }
  if (res.status === 422 || body.code === 'unsupported_country') return { status: 'unsupported_country' };
  return { status: 'failed' };
}

export type AcceptTermsResult =
  | { status: 'ok'; partner: PartnerView }
  | { status: 'terms'; error: TermsError }
  | { status: 'failed' };

/** Re-aceptar los Términos del Programa (la versión vigente) desde el panel. */
export async function acceptPartnerTerms(termsVersion: string): Promise<AcceptTermsResult> {
  let res: Response;
  try {
    res = await apiFetch('/job-apply/api/partner/accept-terms', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(termsAcceptanceBody(termsVersion)),
    });
  } catch {
    return { status: 'failed' };
  }
  const body = (await res.json().catch(() => ({}))) as { partner?: PartnerView };
  if (res.ok && body.partner) return { status: 'ok', partner: body.partner };
  const termsError = readTermsError(res.status, body);
  if (termsError) return { status: 'terms', error: termsError };
  return { status: 'failed' };
}
